import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Inbox } from "lucide-react";
import { InputVenture } from "@/components/ui/input-venture";
import { VentureCard, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { useTickets } from "@/hooks/useTickets";
import { PageTour } from "@/components/tour/PageTour";
import { supportTicketsSteps } from "@/lib/tour-steps";
import { formatDistanceToNow } from "date-fns";

type FilterTab = 'all' | 'open' | 'in_progress' | 'resolved' | 'closed';

const filterTabs: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'closed', label: 'Closed' },
];

function priorityBadgeVariant(priority: string) {
  switch (priority) {
    case 'urgent': return 'destructive';
    case 'high': return 'warning';
    case 'medium': return 'info';
    case 'low': return 'default';
    default: return 'default';
  }
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'open': return 'warning';
    case 'in_progress': return 'info';
    case 'resolved': return 'success';
    case 'closed': return 'default';
    default: return 'default';
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'in_progress': return 'In Progress';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

export default function SupportTickets() {
  const navigate = useNavigate();
  const { tickets, loading } = useTickets();

  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');

  const filtered = tickets.filter((t) => {
    const matchesTab = activeTab === 'all' || t.status === activeTab;
    const q = search.toLowerCase();
    const matchesSearch = !q || t.subject.toLowerCase().includes(q) || (t.client_name || '').toLowerCase().includes(q);
    return matchesTab && matchesSearch;
  });

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <PageTour steps={supportTicketsSteps} />
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Support Tickets</h1>
        <p className="text-muted-foreground">Manage and respond to client support requests</p>
      </div>

      {/* Filter Tabs + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-1 border border-border rounded-lg p-1 bg-surface w-fit">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto w-full sm:w-72">
          <InputVenture
            icon={<Search className="w-4 h-4" />}
            placeholder="Search by subject or client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Tickets Table */}
      <VentureCard>
        <VentureCardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-12 rounded-lg bg-muted/30 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Inbox className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No tickets found</p>
              <p className="text-xs mt-1">
                {search ? 'Try a different search term' : 'No tickets match this filter'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Client</th>
                    <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Subject</th>
                    <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden sm:table-cell">Category</th>
                    <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">Priority</th>
                    <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => navigate(`/admin/support-tickets/${ticket.id}`)}
                      className="border-b border-border last:border-0 hover:bg-surface/50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium">{ticket.client_name || '—'}</td>
                      <td className="px-4 py-3 text-sm max-w-[200px] truncate">{ticket.subject}</td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs text-muted-foreground capitalize">{ticket.category.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <BadgeVenture variant={priorityBadgeVariant(ticket.priority)} className="text-xs capitalize">
                          {ticket.priority}
                        </BadgeVenture>
                      </td>
                      <td className="px-4 py-3">
                        <BadgeVenture variant={statusBadgeVariant(ticket.status)} className="text-xs">
                          {statusLabel(ticket.status)}
                        </BadgeVenture>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground hidden lg:table-cell">
                        {formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
  );
}
